import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AndroidBluetoothMidi,
  connectPairedBluetoothKeyboard,
  findPairedBluetoothKeyboards,
  initialBluetoothKeyboardStep,
  isAndroidBluetoothMidiAvailable,
  stepAfterConnectionChange,
} from 'Utils/AndroidBluetoothMidi';
import type { BluetoothKeyboardStep, PairedBluetoothKeyboard } from 'Utils/AndroidBluetoothMidi';

// MUS-450 - Settings > Keyboard on Android: connect a Bluetooth MIDI keyboard that is
// already paired in Android's Bluetooth settings. Renders nothing on iOS and the web.

const headingSx = {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  letterSpacing: '0.01em',
  color: '#ECECEC',
  marginBottom: '10px',
};

const messageSx = {
  fontSize: '14px',
  lineHeight: '20px',
  color: 'rgba(236,236,236,0.8)',
  marginBottom: '10px',
};

const primaryButtonSx = {
  px: '20px',
  py: '10px',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: '#4A90D9',
  color: '#FFFFFF',
  fontSize: '15px',
  fontWeight: 'bold',
  fontFamily: 'Lato',
  '&:hover': { opacity: 0.85 },
};

const secondaryButtonSx = {
  ...primaryButtonSx,
  backgroundColor: 'transparent',
  border: '1px solid rgba(255,255,255,0.28)',
  color: '#ECECEC',
};

type ActionButtonProps = {
  label: string;
  onPress: () => void;
  secondary?: boolean;
};

const ActionButton = ({ label, onPress, secondary = false }: ActionButtonProps) => (
  <Box
    component="button"
    type="button"
    onClick={onPress}
    sx={secondary ? secondaryButtonSx : primaryButtonSx}
  >
    {label}
  </Box>
);

const Message = ({ children }: { children: ReactNode }) => (
  <Box sx={messageSx} role="status">
    {children}
  </Box>
);

const ButtonRow = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>{children}</Box>
);

export const AndroidBluetoothKeyboardPanel = () => {
  const [step, setStep] = useState<BluetoothKeyboardStep>({ kind: 'idle' });

  useEffect(() => {
    let active = true;
    void initialBluetoothKeyboardStep().then((initialStep) => {
      if (active) setStep((current) => (current.kind === 'idle' ? initialStep : current));
    });
    const listenerHandle = AndroidBluetoothMidi.addListener('connectionChanged', (change) => {
      if (active) setStep((current) => stepAfterConnectionChange(current, change));
    });
    return () => {
      active = false;
      void listenerHandle.then((handle) => handle.remove());
    };
  }, []);

  const findKeyboards = useCallback(async () => {
    setStep({ kind: 'checking' });
    setStep(await findPairedBluetoothKeyboards());
  }, []);

  const connectKeyboard = useCallback(async (keyboard: PairedBluetoothKeyboard) => {
    setStep({ kind: 'connecting', name: keyboard.name });
    setStep(await connectPairedBluetoothKeyboard(keyboard));
  }, []);

  const disconnectKeyboard = useCallback(async () => {
    try {
      await AndroidBluetoothMidi.disconnect();
    } finally {
      setStep({ kind: 'idle' });
    }
  }, []);

  const openBluetoothSettings = useCallback(() => {
    void AndroidBluetoothMidi.openBluetoothSettings().catch(() => undefined);
  }, []);

  const openAppSettings = useCallback(() => {
    void AndroidBluetoothMidi.openAppSettings().catch(() => undefined);
  }, []);

  const tryAgain = (
    <ActionButton
      label="Try again"
      onPress={() => {
        void findKeyboards();
      }}
    />
  );

  const renderStep = () => {
    switch (step.kind) {
      case 'checking':
        return <Message>Looking for your keyboard...</Message>;
      case 'unsupported':
        return <Message>This device can&apos;t connect to Bluetooth MIDI keyboards.</Message>;
      case 'bluetooth-off':
        return (
          <>
            <Message>Bluetooth is off. Turn it on, then try again.</Message>
            <ButtonRow>
              {tryAgain}
              <ActionButton
                label="Open Bluetooth settings"
                onPress={openBluetoothSettings}
                secondary
              />
            </ButtonRow>
          </>
        );
      case 'permission-denied':
        return (
          <>
            <Message>
              MuseFlow needs the Nearby devices permission to connect to a Bluetooth keyboard. A USB
              keyboard works without it. If Android doesn&apos;t ask again, open MuseFlow&apos;s
              settings and allow Nearby devices.
            </Message>
            <ButtonRow>
              {tryAgain}
              <ActionButton label="Open app settings" onPress={openAppSettings} secondary />
            </ButtonRow>
          </>
        );
      case 'no-keyboards':
        return (
          <>
            <Message>
              No paired Bluetooth keyboard found. Pair your keyboard in Android&apos;s Bluetooth
              settings, then try again.
            </Message>
            <ButtonRow>
              {tryAgain}
              <ActionButton
                label="Open Bluetooth settings"
                onPress={openBluetoothSettings}
                secondary
              />
            </ButtonRow>
          </>
        );
      case 'choose':
        return (
          <>
            <Message>Choose your keyboard:</Message>
            <ButtonRow>
              {step.keyboards.map((keyboard) => (
                <ActionButton
                  key={keyboard.id}
                  label={keyboard.name}
                  onPress={() => {
                    void connectKeyboard(keyboard);
                  }}
                  secondary={!keyboard.midi}
                />
              ))}
            </ButtonRow>
          </>
        );
      case 'connecting':
        return <Message>Connecting to {step.name}...</Message>;
      case 'connected':
        return (
          <>
            <Message>Connected to {step.name}.</Message>
            <ActionButton
              label="Disconnect"
              onPress={() => {
                void disconnectKeyboard();
              }}
              secondary
            />
          </>
        );
      case 'failed':
        return (
          <>
            <Message>
              Couldn&apos;t connect to {step.name}. Make sure it is on and nearby, then try again.
            </Message>
            {tryAgain}
          </>
        );
      case 'lost':
      case 'idle':
      case 'error':
        return (
          <>
            {step.kind === 'lost' && <Message>Lost the connection to {step.name}.</Message>}
            {step.kind === 'error' && <Message>Something went wrong. Try again.</Message>}
            {step.kind === 'idle' && (
              <Message>
                Pair your Bluetooth MIDI keyboard in Android&apos;s Bluetooth settings first, then
                connect it here.
              </Message>
            )}
            <ActionButton
              label="Connect Bluetooth keyboard"
              onPress={() => {
                void findKeyboards();
              }}
            />
          </>
        );
    }
  };

  return (
    <Box sx={{ marginBottom: '24px', fontFamily: 'Lato' }} data-testid="android-bluetooth-keyboard">
      <Box sx={headingSx}>Bluetooth keyboard</Box>
      {renderStep()}
    </Box>
  );
};

const AndroidBluetoothKeyboard = () =>
  isAndroidBluetoothMidiAvailable() ? <AndroidBluetoothKeyboardPanel /> : null;

export default AndroidBluetoothKeyboard;
